import { X } from 'lucide-react'
import { useState } from 'react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useCreateCategory,
  useUpdateCategory,
  type CategoryWithChildren,
  type CategoryType,
  type CreateCategoryInput,
} from '@/hooks'

interface CategoryFormProps {
  category?: CategoryWithChildren
  defaultType?: CategoryType
  parentId?: string
  onClose: () => void
  onSuccess: () => void
}

export function CategoryForm({
  category,
  defaultType = 'expense',
  parentId,
  onClose,
  onSuccess,
}: CategoryFormProps) {
  const isEditing = !!category
  const [formData, setFormData] = useState<CreateCategoryInput>({
    name: category?.name ?? '',
    type: category?.type ?? defaultType,
    parentId: category?.parentId ?? parentId,
    description: category?.description ?? '',
    sortOrder: category?.sortOrder ?? 0,
  })
  const [error, setError] = useState<string | null>(null)

  const createMutation = useCreateCategory()
  const updateMutation = useUpdateCategory()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const submitAsync = async () => {
      try {
        if (isEditing && category) {
          await updateMutation.mutateAsync({
            id: category.id,
            data: {
              name: formData.name,
              description: formData.description || undefined,
              sortOrder: formData.sortOrder,
            },
          })
        } else {
          await createMutation.mutateAsync(formData)
        }
        onSuccess()
      } catch (err) {
        setError(err instanceof Error ? err.message : '操作失败')
      }
    }

    void submitAsync()
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">
            {isEditing ? '编辑分类' : parentId ? '新建子分类' : '新建分类'}
          </h2>
          <button onClick={onClose} className="rounded p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium">分类名称</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="请输入分类名称"
              required
            />
          </div>

          {!isEditing && !parentId && (
            <div>
              <label className="mb-1 block text-sm font-medium">分类类型</label>
              <Select
                value={formData.type}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, type: value as CategoryType }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="选择分类类型" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">收入</SelectItem>
                  <SelectItem value="expense">支出</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium">描述</label>
            <textarea
              value={formData.description ?? ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="可选描述"
              rows={2}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">排序</label>
            <input
              type="number"
              value={formData.sortOrder}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, sortOrder: parseInt(e.target.value) || 0 }))
              }
              className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="0"
            />
          </div>

          {error && <div className="text-sm text-destructive">{error}</div>}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? '保存中...' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
